import { serviceConstants, errorConstants } from './utils/constant';

interface ErrorLogData {
  type: string;
  message?: string;
  error?: string | object;
  reason?: string | object;
  stack?: string;
  status?: number;
  statusText?: string;
  url?: string;
  file?: string;
  line?: number;
  column?: number;
  requestBody?: any;
  requestHeaders?: Record<string, string>;
  timestamp?: string;
  userAgent?: string;
  username?: string;

  stackFrames?: Array<{
    fileName?: string;
    lineNumber?: number;
    columnNumber?: number;
    functionName?: string;
  }>;
}

export const getAccessTokenHandler = () => {
  const token = JSON.parse(
    localStorage.getItem(serviceConstants.OKTA_TOKEN_KEY) as string
  )?.accessToken;
  if (token) {
    return token;
  } else {
    return ' ';
  }
};

export const getPagerdutyToken = async () => {
  try {
    const response = await fetch(import.meta.env.VITE_APP_GET_PAGERDUTY_TOKEN_API as string, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessTokenHandler().accessToken}`
      },
      body: JSON.stringify({})
    });
    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    throw new Error(errorConstants.SERVER_ERROR);
  }
};

export const postPagerDutyOnGoingIncidentList = async (token: string) => {
  try {
    const response = await fetch(
      import.meta.env.VITE_APP_POST_PAGERDUTY_ONGOING_INCIDENT_LIST_API as string,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAccessTokenHandler().accessToken}`,
          'x-pagerduty-token': token
        },
        body: JSON.stringify({})
      }
    );
    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    throw new Error(errorConstants.SERVER_ERROR);
  }
};

export const postPagerDutyOnCallEngineer = async (token: string) => {
  try {
    const response = await fetch(
      import.meta.env.VITE_APP_POST_PAGERDUTY_ONCALL_ENGINEER_API as string,
      {
        method: 'POST',
        body: JSON.stringify({ size: '1' }),
        headers: {
          Authorization: `Bearer ${getAccessTokenHandler().accessToken}`,
          'x-pagerduty-token': token
        }
      }
    );
    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    throw new Error(errorConstants.SERVER_ERROR);
  }
};

export const postPagerDutyCreateIncident = async (
  token: string,
  summary: string,
  username: string,
  isTestIncident: boolean
) => {
  try {
    const response = await fetch(
      import.meta.env.VITE_APP_POST_PAGERDUTY_CREATE_INCIDENT_API as string,
      {
        method: 'POST',
        body: JSON.stringify({
          detailString: summary,
          username: username,
          isTestIncident: isTestIncident
        }),
        headers: {
          Authorization: `Bearer ${getAccessTokenHandler().accessToken}`,
          'x-pagerduty-token': token
        }
      }
    );
    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    throw new Error(errorConstants.SERVER_ERROR);
  }
};

// Batch queue and timer
let errorQueue: ErrorLogData[] = [];
let batchTimer: ReturnType<typeof setTimeout> | null = null;

// Flush interval in milliseconds
const FLUSH_INTERVAL = 5000; // send batch every 5 seconds

export const logErrorToServer = (errorData: ErrorLogData) => {
  // Add the new error to the queue
  errorQueue.push({
    ...errorData,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    username:
      JSON.parse(localStorage.getItem(serviceConstants.OKTA_TOKEN_KEY) ?? '{}').idToken?.claims
        ?.sAMAccountName || ''
  });

  // If a timer is already set, do nothing else
  if (batchTimer) {
    return;
  }

  // Otherwise, set a timer to send all queued errors after FLUSH_INTERVAL
  batchTimer = setTimeout(async () => {
    if (errorQueue.length === 0) {
      batchTimer = null;
      return;
    }

    // Copy and clear the queue
    const errorsToSend = [...errorQueue];
    errorQueue = [];
    batchTimer = null;

    try {
      const token = getAccessTokenHandler();
      await fetch(import.meta.env.VITE_APP_POST_FRONTEND_ERROR_LOGGER_API as string, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.accessToken || token}`
        },
        body: JSON.stringify(errorsToSend.length > 0 && errorsToSend[0])
      });
    } catch (error) {
      console.error('Failed to log batch errors to backend:', error);
      // If you want, you can re-add errorsToSend back to errorQueue for retry later
    }
  }, FLUSH_INTERVAL);
};
